import {
  Component,
  OnInit,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { HttpErrorResponse } from '@angular/common/http';
import { AppModelModalComponent } from '@nimmsta/ng-commons-lib/forms';
import {
  AppDialogRef,
  AppDialogService,
} from '@nimmsta/ng-commons-lib/services/app-dialog';
import { AuthorizationService } from '@nimmsta/ng-commons-lib/auth';
import {
  Column,
  DataTableComponent,
  EmptyTableComponent,
  IconAction,
  NimmstaDataSource,
} from '@nimmsta/ng-commons-lib/data-table';
import { ErrorComponent } from '@nimmsta/ng-commons-lib/error';
import { HeaderBarComponent } from '@nimmsta/ng-commons-lib/header-bar';
import { NimmstaTimeAgoPipe } from '@nimmsta/ng-commons-lib/pipes';
import { Workflow, WorkflowStatus } from '@nimmsta/ng-commons-lib/models';
import { WorkflowService } from '@nimmsta/ng-commons-lib/services/workflow';
import { Router } from '@angular/router';
import { WorkflowDefinitionComponent } from './workflow-definition.component';
import { WorkflowFormComponent } from './workflow-form.component';
import { ShowWorkflowIconAction } from './showWorkflowIconAction';
import { ShowWorkflowComponent } from './show-workflow.component';
import { hasWorkflowMediaContent } from './workflow-media-preview.util';

_('workflow.title');

@Component({
  selector: 'app-workflow',
  templateUrl: './workflow.component.html',
  imports: [
    WorkflowFormComponent,
    TranslatePipe,
    TranslatePipe,
    DataTableComponent,
    AppModelModalComponent,
    EmptyTableComponent,
    ErrorComponent,
    HeaderBarComponent,
  ],
  providers: [NimmstaTimeAgoPipe],
})
export class WorkflowComponent implements OnInit {
  private authorizationService = inject(AuthorizationService);
  private service = inject(WorkflowService);
  private translate = inject(TranslateService);
  private modalService = inject(AppDialogService);
  private timeago = inject(NimmstaTimeAgoPipe);
  private router = inject(Router);

  source!: NimmstaDataSource<Workflow>;

  @ViewChild(DataTableComponent, { static: true })
  private tableComponent!: DataTableComponent<Workflow>;

  @ViewChild('EditModal', { static: true })
  private editModalComponent!: AppModelModalComponent<Workflow>;

  @ViewChild(WorkflowFormComponent, { static: true })
  private formComponent!: WorkflowFormComponent;

  @ViewChild('definitionQuestionModal', { static: true })
  definitionModal!: TemplateRef<unknown>;

  @ViewChild('activateQuestionModal', { static: true })
  activateModal!: TemplateRef<unknown>;

  @ViewChild('errorDeleteModal', { static: true })
  errorDeleteModal!: TemplateRef<unknown>;

  iconActions: IconAction<Workflow>[] = [];

  modalRef?: AppDialogRef;

  error?: HttpErrorResponse;

  deleteError?: HttpErrorResponse;

  selectedWorkflow?: Workflow;

  private currentDefinitionComponent?: WorkflowDefinitionComponent;

  canWriteWorkflows = false;

  get columns(): Column[] {
    return [
      new Column('name', 'workflow.name', { sortable: true }),
      new Column('description', 'workflow.description'),
      new Column('dataConnection.name', 'workflow.dataconnection', {
        sortable: true,
      }),
      new Column('lastUpdatedAt', 'lastUpdatedAt', {
        pipe: this.timeago,
        sortable: true,
        invertSorting: true,
      }),
    ];
  }

  ngOnInit(): void {
    this.canWriteWorkflows =
      this.authorizationService.hasPermission('workflow.write');
    this.source = new NimmstaDataSource<Workflow>(this.service);
    const showAction = new ShowWorkflowIconAction();
    this.iconActions.push(showAction);
    showAction.subscribe((id) => this.show(id));
    const executionAction = new IconAction(
      'workflow.execution.plural',
      'event_list',
    );
    this.iconActions.push(executionAction);
    executionAction.subscribe((id) => this.onExecution(id));
    if (this.canWriteWorkflows) {
      const editAction = new IconAction('edit', 'edit');
      this.iconActions.push(editAction);
      editAction.subscribe((id) => this.edit(id));
      const deleteAction = new IconAction('delete', 'delete', true);
      this.iconActions.push(deleteAction);
      deleteAction.subscribe((id) => this.delete(id));
      const definitionAction = new IconAction(
        'workflow.definition',
        'settings_applications',
      );
      this.iconActions.push(definitionAction);
      definitionAction.subscribe((id) => this.onDefinition(id));
    }
  }

  show(id: string): void {
    const workflow = this.getSelectedObject(id)!;
    if (hasWorkflowMediaContent(workflow)) {
      this.modalService.open(ShowWorkflowComponent, {
        data: {
          workflow,
        },
        size: 'xl',
      });
    } else {
      this.translate.get('workflow.edit').subscribe((value) => {
        this.openModal(value, true, this.getSelectedObject(id)!);
      });
    }
  }

  new(): void {
    this.translate.get('workflow.new').subscribe((value) => {
      const workflow = {} as Workflow;
      this.openModal(value, true, workflow);
    });
  }

  edit(id: string): void {
    this.translate.get('workflow.edit').subscribe((value) => {
      this.openModal(value, true, this.getSelectedObject(id)!);
    });
  }

  openModal(title: string, editable: boolean, value: Workflow): void {
    this.formComponent.editable = editable;
    this.formComponent.setValue(value);
    this.editModalComponent.title = title;
    this.editModalComponent.editable = editable;
    this.editModalComponent.show();
  }

  delete(id: string): void {
    this.service.delete(this.getSelectedObject(id)!).subscribe({
      next: () => {
        this.tableComponent.loadDataPage();
      },
      error: (error: HttpErrorResponse) => {
        if (error.status === 409) {
          this.deleteError = error;
          setTimeout(() => {
            this.modalRef = this.modalService.open(this.errorDeleteModal);
          }, 150);
        } else {
          this.error = error;
        }
      },
    });
  }

  getDeleteWorkers(): string[] {
    return this.deleteError?.error.exc_message.split(',') || [];
  }

  onSave(): void {
    this.formComponent.onSave();
  }

  onModalClose(): void {
    this.editModalComponent.hide();
    this.tableComponent.loadDataPage();
  }

  getSelectedObject(id: string): Workflow | undefined {
    return this.source.data?.content.filter((d) => id === d.id).pop();
  }

  onLoading(loading: boolean): void {
    this.editModalComponent.loading = loading;
  }

  onDefinition(id: string): void {
    this.selectedWorkflow = this.getSelectedObject(id)!;
    this.modalRef = this.modalService.open(this.definitionModal);
  }

  showDefinition() {
    this.modalRef?.close();
    const workflow = this.selectedWorkflow!;
    this.router.navigate(['/workflows', workflow.id, 'definition'], {
      state: { workflow },
    });
  }

  isActive(workflow?: Workflow): boolean {
    return workflow?.status === WorkflowStatus.ACTIVE;
  }

  onActivateDeactivate(id: string): void {
    this.selectedWorkflow = this.getSelectedObject(id)!;
    this.modalRef = this.modalService.open(this.activateModal);
  }

  toggleWorkflowStatus() {
    this.modalRef?.close();
    this.service.activateDeactivateWorkflow(this.selectedWorkflow!).subscribe({
      next: () => {
        this.tableComponent.loadDataPage();
      },
      error: (error: HttpErrorResponse) => {
        this.error = error;
      },
    });
  }

  onExecution(id: string): void {
    this.router.navigate(['/workflows', id, 'executions']);
  }
}
